import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = ["disabled"]
const _hoisted_2 = {
  key: 0,
  class: "flex items-center",
  role: "status"
}
const _hoisted_3 = {
  key: 1,
  class: "[&>svg]:stroke-current [&>svg]:h-5 [&>svg]:w-5"
}

import { ref } from 'vue';
import * as XLSX from 'xlsx';
import { useLearnerStore } from '@/stores/learnerStore';
import { useSchoolStore } from '@/stores/schoolStore';
import { showToast } from '@/utils/toastUtils';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/vue/24/outline';
import { Learner } from '@/interfaces/learner.interface';


export default /*#__PURE__*/_defineComponent({
  __name: 'ExportLearnersButtonComponent',
  setup(__props) {

const learnerStore = useLearnerStore();
const schoolStore = useSchoolStore();
const isExporting = ref(false);

// --- NEW HELPER FUNCTION ---
/**
 * Iterates over the first row of a worksheet and applies a bold font style.
 * @param worksheet The worksheet to modify.
 * @param data The array of objects used to generate the sheet, to get header keys.
 */
const addBoldHeaders = (worksheet: XLSX.WorkSheet, data: any[]) => {
  if (!data || data.length === 0) return;

  // Get the headers from the keys of the first data object
  const headers = Object.keys(data[0]);
  headers.forEach((_, index) => {
    // Construct the cell address (e.g., A1, B1, C1)
    const cellAddress = XLSX.utils.encode_cell({ c: index, r: 0 });
    
    // Ensure the cell exists before styling
    if (worksheet[cellAddress]) {
      // Apply the bold style
      worksheet[cellAddress].s = { font: { bold: true } };
    }
  });
};

const handleExport = async () => {
  isExporting.value = true;

  const schoolId = schoolStore.currentSchoolStructure?._id;
  if (!schoolId) {
    showToast({
      text: 'No school selected. Please select a school first.',
      type: 'error',
      icon: ExclamationCircleIcon,
    });
    isExporting.value = false;
    return;
  }
  
  try {
    // Get all learners (including archived)
    const allLearners = await learnerStore.exportSchoolLearners(schoolId);
    
    // Separate active and archived students
    const activeStudents = allLearners.filter(
      learner => learner.status === 'active' || learner.status === 'deactivated'
    );
    const archivedStudents = allLearners.filter(
      learner => learner.status === 'archived'
    );
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Create active students sheet
    const activeData = await prepareActiveStudentsData(activeStudents);
    const wsActive = XLSX.utils.json_to_sheet(activeData);

    // Make the headers bold for the "Students" sheet
    addBoldHeaders(wsActive, activeData);
    
    // Apply column widths and formatting
    wsActive['!cols'] = [
      { wch: 15 }, // First Name
      { wch: 15 }, // Surname
      { wch: 30 }, // Username/Email
      { wch: 15 }, // Password
      { wch: 20 }, // Maths Class
      { wch: 15 }, // Maths Class Grade
      { wch: 20 }, // Maths Last Active
      { wch: 20 }, // English Class
      { wch: 15 }, // English Class Grade
      { wch: 20 }, // English Last Active
      { wch: 20 }, // Maths Assessment Level
      { wch: 20 }, // English Assessment Level
      { wch: 15 }, // Learning Mode
      { wch: 15 }, // Language
      { wch: 20 }, // Will repeat the current Grade/Year
    ];

    // Add AutoFilter to the active students sheet
    const activeRange = XLSX.utils.decode_range(wsActive['!ref'] || 'A1');
    wsActive['!autofilter'] = { ref: XLSX.utils.encode_range(activeRange) };
    
    // Add sheet to workbook
    XLSX.utils.book_append_sheet(wb, wsActive, 'Students');
    
    // Create archived students sheet
    const archivedData = prepareArchivedStudentsData(archivedStudents);
    const wsArchived = XLSX.utils.json_to_sheet(archivedData);

    // --- ADDED ---
    // Make the headers bold for the "ArchivedStudents" sheet
    addBoldHeaders(wsArchived, archivedData);
    
    // Apply column widths for archived sheet
    wsArchived['!cols'] = [
      { wch: 15 }, // First Name
      { wch: 15 }, // Surname
      { wch: 30 }, // Username/Email
      { wch: 20 }, // Maths Last Active
      { wch: 20 }, // English Last Active
    ];

    // Add AutoFilter to the archived students sheet
    const archivedRange = XLSX.utils.decode_range(wsArchived['!ref'] || 'A1');
    wsArchived['!autofilter'] = { ref: XLSX.utils.encode_range(archivedRange) };
    
    // Add archived sheet to workbook
    XLSX.utils.book_append_sheet(wb, wsArchived, 'ArchivedStudents');
    
    // Generate filename with school name and date
    const schoolName = schoolStore.currentSchoolStructure?.name || 'School';
    const date = new Date().toISOString().split('T')[0];
    const filename = `${schoolName}_Students_Export_${date}.xlsx`;
    
    // Write file
    XLSX.writeFile(wb, filename);
    
    showToast({
      text: 'Students exported successfully',
      type: 'success',
      icon: CheckCircleIcon,
    });
  } catch (error) {
    console.error('Export failed:', error);
    showToast({
      text: 'Failed to export students',
      type: 'error',
      icon: ExclamationCircleIcon,
    });
  } finally {
    isExporting.value = false;
  }
};

const prepareActiveStudentsData = async (students: Learner[]) => {
  const data = [];
  
  for (const student of students) {
    // Get maths and english classes
    const mathsClasses = student.classes?.filter(cls => cls.subject.code === 'MATHS') || [];
    const englishClasses = student.classes?.filter(cls => cls.subject.code === 'ENGLISH') || [];
    
    // Get assessment states for each subject
    const mathsAssessment = student.assessmentStates?.find(
      state => state.subject?.toLowerCase() === 'maths'
    );
    const englishAssessment = student.assessmentStates?.find(
      state => state.subject?.toLowerCase() === 'english'
    );
    
    // Format dates
    // const formatDate = (dateValue: Date | string | undefined) => {
    //   if (!dateValue) return null;
    //   const date = new Date(dateValue);
    //   return isNaN(date.getTime()) ? null : date.toLocaleDateString();
    // };
    // UPDATED: This now returns a Date object or null
    const formatDate = (dateValue: Date | string | undefined): Date | null => {
      if (!dateValue) return null;
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    };

    /**
     * Formats the assessment level type into a more readable string.
     */
    const formatAssessmentLevel = (assessmentState: any, subject: 'MATHS' | 'ENGLISH'): string => {
        if (!assessmentState || !assessmentState.type) return '';
        // Example: "MATHS_JUNIOR_PRIMARY" becomes "Junior Primary"
        return assessmentState.type
            .replace(`${subject}_`, '')
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (l: any) => l.toUpperCase());
    };
    
    // Get decrypted password if needed
    let password = student.password || '';
    // if (!password && student._id) {
    //   try {
    //     password = await learnerStore.fetchDecryptedPassword(student._id);
    //   } catch (error) {
    //     console.error(`Failed to decrypt password for student ${student._id}`);
    //     password = '******';
    //   }
    // }

    // UPDATED: Get class names and provide a fallback text
    const mathsClassNames = mathsClasses.map(cls => cls.name).join(', ');
    const englishClassNames = englishClasses.map(cls => cls.name).join(', ');
    
    // Prepare row data
    const row = {
      'First Name': student.firstName || '',
      'Surname': student.lastName || '',
      'Username/Email': student.email || '',
      'Password': password,
      'Maths Class':  mathsClassNames || 'Unassigned',
      'Maths Class Grade': mathsClasses.length > 0 && mathsClasses[0].gradeLevel ? Number(mathsClasses[0].gradeLevel) : null,
      'Maths Last Active': formatDate(mathsAssessment?.lastActivity),
      'English Class': englishClassNames || 'Unassigned',
      'English Class Grade': englishClasses.length > 0 && englishClasses[0].gradeLevel ? Number(englishClasses[0].gradeLevel) : null,
      'English Last Active': formatDate(englishAssessment?.lastActivity),
      'Maths Assessment Level': formatAssessmentLevel(mathsAssessment, 'MATHS') || '',
      'English Assessment Level': formatAssessmentLevel(englishAssessment, 'ENGLISH') || '',
      'Learning Mode': student.learningMode === 'default' ? 'Class Default' : (student.learningMode || 'Class Default'),
      'Language': student.preferredLang === 'default' || !student.preferredLang ? 'Class Default' : student.preferredLang,
      'Will repeat the current Grade/Year': student.repeatingGrade ? 'TRUE' : 'FALSE'
    };
    
    data.push(row);
  }
  
  return data;
};

const prepareArchivedStudentsData = (students: Learner[]) => {
  return students.map(student => {
    // Get assessment states for each subject
    const mathsAssessment = student.assessmentStates?.find(
      state => state.subject?.toLowerCase() === 'maths'
    );
    const englishAssessment = student.assessmentStates?.find(
      state => state.subject?.toLowerCase() === 'english'
    );
    
    // UPDATED: This now returns a Date object or null
    const formatDate = (dateValue: Date | string | undefined): Date | null => {
      if (!dateValue) return null;
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    };
    
    return {
      'First Name': student.firstName || '',
      'Surname': student.lastName || '',
      'Username/Email': student.email || '',
      'Maths Last Active': formatDate(mathsAssessment?.lastActivity),
      'English Last Active': formatDate(englishAssessment?.lastActivity)
    };
  });
};

return (_ctx: any,_cache: any) => {
  const _component_spinner_loader = _resolveComponent("spinner-loader")!
  const _component_rl_icon = _resolveComponent("rl-icon")!

  return (_openBlock(), _createElementBlock("button", {
    onClick: handleExport,
    class: "text-gray-700 flex items-center gap-1",
    disabled: isExporting.value
  }, [
    (isExporting.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_spinner_loader, { color: 'text-[#155EEF]' })
        ]))
      : (_openBlock(), _createElementBlock("span", _hoisted_3, [
          _createVNode(_component_rl_icon, {
            icon: 'rl-export',
            title: 'rl export',
            "aria-hidden": "true"
          })
        ])),
    _createTextVNode(" Export ")
  ], 8, _hoisted_1))
}
}

})